import React, { useState } from 'react';
import { CustomActivity, DailyRecord } from '../types';
import { ClipboardList, Plus, Trash2, Clock, Flame, HeartPulse } from 'lucide-react';
import { useLanguage, formatNumberByLang } from '../lib/i18n';

interface ActivityLogProps {
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
}

// Preset ideas for rapid adding in 3 languages
const ACTIVITY_PRESETS_BY_LANG: Record<'fa' | 'en' | 'sv', Array<{ title: string; duration: number; calories: number; notes: string }>> = {
  fa: [
    { title: 'دوچرخه‌سواری ثابت', duration: 30, calories: 180, notes: 'پا زدن ملایم روی دوچرخه ثابت، با صاف و تراز نگه داشتن پایین ستون فقرات.' },
    { title: 'پیاده‌روی در فضای باز', duration: 25, calories: 110, notes: 'پیاده‌روی نسبتاً سریع در پارک یا پیاده‌رو جهت کمک به آب کردن چربی‌های موضعی شکم.' },
    { title: 'ویزیت پزشک / فیزیوتراپی', duration: 45, calories: 0, notes: 'ارزیابی وضعیت مفاصل پایین کمر و تمرینات کششی دستی درمانگر.' },
    { title: 'خرید خواروبار و لوازم منزل', duration: 35, calories: 75, notes: 'خرید روزانه با احتیاط کامل در بلند کردن اجسام و حفظ تقارن شانه.' },
    { title: 'انجام تکالیف سوئدی SFI', duration: 45, calories: 0, notes: 'حل تمارین نوشتاری و صوتی ارائه‌شده توسط معلم سوئدی.' },
    { title: 'کاهش تنش و مطالعه کتاب', duration: 30, calories: 0, notes: 'دراز کشیدن به پشت و مطالعه آرامش‌بخش برای رفع تنش‌های عصبی کمر.' }
  ],
  en: [
    { title: 'Stationary Cycling', duration: 30, calories: 180, notes: 'Gentle pedaling with proper lower spine alignment.' },
    { title: 'Outdoor Brisk Walk', duration: 25, calories: 110, notes: 'Brisk walk in the park or pavement to support abdominal fat burning.' },
    { title: 'Doctor / Physiotherapy Visit', duration: 45, calories: 0, notes: 'Lumbar spine assessment and manual stretches with therapist.' },
    { title: 'Grocery & Home Shopping', duration: 35, calories: 75, notes: 'Daily errands with mindful lifting and shoulder balance.' },
    { title: 'SFI Swedish Homework', duration: 45, calories: 0, notes: 'Writing and listening assignments provided by teacher.' },
    { title: 'Relaxation & Book Reading', duration: 30, calories: 0, notes: 'Resting supine and reading calmly to relieve nervous tension.' }
  ],
  sv: [
    { title: 'Motionscykel', duration: 30, calories: 180, notes: 'Skonsam cykling på motionscykel med rak och balanserad ländrygg.' },
    { title: 'Utomhuspromenad', duration: 25, calories: 110, notes: 'Rask promenad i parken för att stimulera fettförbränning.' },
    { title: 'Läkar- / Fysioterapibesök', duration: 45, calories: 0, notes: 'Bedömning av ländryggen och manuella stretchövningar med terapeut.' },
    { title: 'Mat- och hushållsinköp', duration: 35, calories: 75, notes: 'Dagliga ärenden med försiktiga lyft och balanserade axlar.' },
    { title: 'SFI Svenskaläxor', duration: 45, calories: 0, notes: 'Skriv- och lyssningsövningar från svenskläraren.' },
    { title: 'Avkoppling och läsning', duration: 30, calories: 0, notes: 'Avlastande ryggläge och lugn läsning för nervös avspänning.' }
  ]
};

export default function ActivityLogModule({ record, onUpdateRecord }: ActivityLogProps) {
  const { lang, t } = useLanguage();
  const [title, setTitle] = useState('');
  const [startTime, setStartTime] = useState('12:00');
  const [endTime, setEndTime] = useState('12:30');
  const [duration, setDuration] = useState('30');
  const [notes, setNotes] = useState('');
  const [caloriesBurned, setCaloriesBurned] = useState('');

  const handleAddActivity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const newItem: CustomActivity = {
      id: `act_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: title.trim(),
      startTime,
      endTime,
      duration: Number(duration) || 0,
      notes: notes.trim(),
      caloriesBurned: caloriesBurned ? Number(caloriesBurned) : undefined
    };

    onUpdateRecord({
      ...record,
      activities: [...record.activities, newItem]
    });

    // Reset inputs
    setTitle('');
    setStartTime('12:00');
    setEndTime('12:30');
    setDuration('30');
    setNotes('');
    setCaloriesBurned('');
  };

  const handleAddPreset = (p: { title: string; duration: number; calories: number; notes: string }) => {
    const now = new Date();
    const currentHrs = String(now.getHours()).padStart(2, '0');
    const currentMins = String(now.getMinutes()).padStart(2, '0');
    
    // Calculate simulated end time
    const end = new Date(now.getTime() + p.duration * 60000);
    const endHrs = String(end.getHours()).padStart(2, '0');
    const endMins = String(end.getMinutes()).padStart(2, '0');

    const newItem: CustomActivity = {
      id: `act_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: p.title,
      startTime: `${currentHrs}:${currentMins}`,
      endTime: `${endHrs}:${endMins}`,
      duration: p.duration,
      notes: p.notes,
      caloriesBurned: p.calories > 0 ? p.calories : undefined
    };

    onUpdateRecord({
      ...record,
      activities: [...record.activities, newItem]
    });
  };

  const handleDeleteActivity = (id: string) => {
    onUpdateRecord({
      ...record,
      activities: record.activities.filter(item => item.id !== id)
    });
  };

  // Totals
  const totalActiveMinutes = record.activities.reduce((sum, item) => sum + item.duration, 0);
  const totalActiveCalories = record.activities.reduce((sum, item) => sum + (item.caloriesBurned || 0), 0);
  const presets = ACTIVITY_PRESETS_BY_LANG[lang] || ACTIVITY_PRESETS_BY_LANG.fa;

  return (
    <div id="activity-log-module-view" className="space-y-6 animate-fadeIn">
      
      {/* Header */}
      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-2xl font-semibold font-display text-gray-900 dark:text-white flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-indigo-500" />
            {t('activityLogTitle', 'ماژول ۷ — دفتر ثبت فعالیت‌های روزانه')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('activityLogDesc', 'رفت‌وآمدها، مراجعات فیزیوتراپی، سرگرمی، خرید و یادگیری‌های متفرقه خود را ثبت کنید تا همواره تراز فیزیکی ستون فقرات در نظر گرفته شود.')}
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* Active Minutes */}
        <div className="premium-card p-5 dark:bg-gray-800 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">
              {t('activityLogActiveTime', 'زمان فعالیت فرعی اضافی')}
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold font-mono text-indigo-600 dark:text-indigo-400">
                {formatNumberByLang(totalActiveMinutes, lang)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('activityLogMinutesToday', 'دقیقه امروز')}
              </span>
            </div>
          </div>
        </div>

        {/* Calories Burned */}
        <div className="premium-card p-5 dark:bg-gray-800 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-orange-500/15 flex items-center justify-center text-orange-600 dark:text-orange-400">
            <Flame className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">
              {t('activityLogEstimatedCalories', 'کالری تخمینی فرعی')}
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold font-mono text-orange-600 dark:text-orange-400">
                {formatNumberByLang(totalActiveCalories, lang)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('activityLogActiveCalories', 'کالری فعال')}
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* Main split */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left: Logger forms */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* Preset Buttons */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
              <HeartPulse className="w-4 h-4 text-rose-500" />
              {t('activityLogPresetsTitle', 'الگوهای ثبت سریع فعالیت روزمره')}
            </h3>
            <div className="flex flex-wrap gap-2 pt-1">
              {presets.map((p, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAddPreset(p)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 hover:bg-indigo-50 hover:border-indigo-200 dark:bg-gray-900/40 dark:hover:bg-indigo-950/20 text-gray-700 dark:text-gray-300 hover:text-indigo-700 dark:hover:text-indigo-300 transition-all font-medium cursor-pointer"
                >
                  <Plus className="w-3 h-3" />
                  {p.title} <span className="text-gray-400">({formatNumberByLang(p.duration, lang)} {t('activityLogMinutesShort', 'دقیقه')})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              {t('activityLogManualFormTitle', 'ثبت دستی فعالیت سفارشی جدید')}
            </h3>
            <form onSubmit={handleAddActivity} className="grid grid-cols-1 md:grid-cols-6 gap-3">
              
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold text-gray-400 block mb-1">
                  {t('activityLogFieldTitle', 'عنوان فعالیت')}
                </label>
                <input 
                  type="text" 
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('activityLogFieldTitlePh', 'مثال: شنای ملایم کرال پشت')} 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-400 block mb-1">
                  {t('activityLogFieldStartTime', 'زمان شروع')}
                </label>
                <input 
                  type="text" 
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  placeholder="12:00" 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-mono"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-400 block mb-1">
                  {t('activityLogFieldEndTime', 'زمان پایان')}
                </label>
                <input 
                  type="text" 
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  placeholder="12:30" 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-mono"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-400 block mb-1">
                  {t('activityLogFieldDuration', 'مدت (دقیقه)')}
                </label>
                <input 
                  type="number" 
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="30" 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-mono"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-400 block mb-1">
                  {t('activityLogFieldCalories', 'کالری فرعی (اختیاری)')}
                </label>
                <input 
                  type="number" 
                  value={caloriesBurned}
                  onChange={(e) => setCaloriesBurned(e.target.value)}
                  placeholder={t('activityLogFieldCaloriesPh', 'مثال: ۱۵۰')} 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white font-mono"
                />
              </div>

              <div className="md:col-span-5">
                <label className="text-[10px] font-bold text-gray-400 block mb-1">
                  {t('activityLogFieldNotes', 'توضیحات بدنی / مشاهدات درد و راحتی')}
                </label>
                <input 
                  type="text" 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('activityLogFieldNotesPh', 'مثال: هیچگونه احساس دردی در مهره‌های کمر نداشتم. عضلات باسن و شکم به خوبی درگیر شدند.')} 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:border-indigo-500 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex items-end">
                <button 
                  type="submit"
                  className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm flex items-center justify-center gap-1 cursor-pointer"
                >
                  <Plus className="w-4 h-4" /> {t('activityLogSaveBtn', 'ذخیره فعالیت')}
                </button>
              </div>

            </form>
          </div>

        </div>

        {/* Right: Added Logs */}
        <div className="lg:col-span-4 space-y-4">
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider block">
              {t('activityLogTodayListTitle', 'لیست فعالیت‌های ثبت‌شده امروز')}
            </h3>

            {record.activities.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-xs">
                {t('activityLogEmptyState', 'هنوز هیچ فعالیت اضافی برای امروز ثبت نشده است. از الگوهای آماده استفاده کنید یا فرم دستی را تکمیل کنید.')}
              </div>
            ) : (
              <div className="space-y-3.5">
                {record.activities.map(act => (
                  <div 
                    key={act.id}
                    className="p-3.5 rounded-xl bg-gray-50/50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800/40 space-y-2 group relative hover:border-indigo-200 dark:hover:border-indigo-950/40 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{act.title}</h4>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5 font-mono">
                          <Clock className="w-3 h-3 text-indigo-400" />
                          {act.startTime} - {act.endTime} &middot; {formatNumberByLang(act.duration, lang)} {t('activityLogMinutesShort', 'دقیقه')}
                        </span>
                      </div>
                      
                      <button 
                        onClick={() => handleDeleteActivity(act.id)}
                        className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-red-50 dark:hover:bg-red-950/20 transition-all cursor-pointer"
                        title={t('activityLogDeleteTooltip', 'حذف فعالیت')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {act.notes && (
                      <p className="text-[11px] text-gray-600 dark:text-gray-300 italic border-r rtl:border-r ltr:border-l border-indigo-200 dark:border-indigo-800/60 rtl:pr-2 ltr:pl-2 leading-relaxed">
                        {act.notes}
                      </p>
                    )}

                    {act.caloriesBurned !== undefined && act.caloriesBurned > 0 && (
                      <div className="flex items-center gap-1 text-[10px] font-bold text-orange-600 dark:text-orange-400">
                        <Flame className="w-3 h-3 fill-current" />
                        {t('activityLogApproxCal', 'چربی‌سوزی تقریبی: ~{{cal}} کالری', {
                          cal: formatNumberByLang(act.caloriesBurned, lang)
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>

    </div>
  );
}
